import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Card, Link as DSLink, Heading, Icon } from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import {
  type PolicyCoverageAndDeductibles,
  labelCoverages,
  policyTypeCoverageMap,
} from "components/renderers/coverage/policy-type-coverage-map";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { usePolicyDetail, usePolicyId } from "data/ss-data-hooks";
import { getKeyDocs, scrollToTop, trackEvent } from "utils";

import { CoreCoverages } from "./core-coverages";
import { KeyDeductibles } from "./key-deductibles";
import styles from "./policy.module.scss";

export const Policy = () => {
  const navigate = useNavigate();
  const ssPolicyId = usePolicyId();
  const { urlPolicyId } = useParams();
  const { data, isLoading } = usePolicyDetail();

  const { userDetails, userPolicyProperties } = data ?? {};
  const { currentTerm } = userDetails ?? { currentTerm: {} };
  const { policyType } = userPolicyProperties ?? {};

  const policyDeclarations = getKeyDocs(
    data?.userDocuments,
    (data?.userBilling?.mortgagees?.length ?? 0) > 0
  ).filter((doc) => doc.name === "policyDeclarations")[0];

  // Give the coverages more descriptive names:
  const labelledCoverages = labelCoverages(currentTerm);
  // Generate which coverages to show based on policyType:
  const coverageMap: PolicyCoverageAndDeductibles =
    policyTypeCoverageMap[policyType ?? ""];

  const { deductibles, liabilityCoverages, propertyCoverages } =
    coverageMap ?? {};

  useEffect(() => {
    if (urlPolicyId !== ssPolicyId) {
      navigate(`/my/policies/${ssPolicyId}`);
      return;
    }
  }, [ssPolicyId, urlPolicyId, navigate]);

  useEffect(() => {
    trackEvent("Policy");
    scrollToTop();
  }, []);

  if (isLoading) {
    return <BaseLayout loading />;
  }

  const CoreCoveragesHeader = () => (
    <div className={styles.coreCoveragesHeaderContainer}>
      <div className={styles.headerWrapper}>
        <Heading size="md">Core coverages</Heading>
      </div>
      {policyDeclarations && (
        <div className={styles.docLink}>
          <span>
            <Icon name="FileDoc" size="sm" />
          </span>{" "}
          <DSLink
            filename="Policy Declarations"
            type="PDF"
            name={policyDeclarations?.label}
            href={`/api/policy/${policyDeclarations?.parentId}/${policyDeclarations?.location}`}
            target="_blank"
            onPress={() => {
              trackEvent("Declaration Page Link Clicked", {
                sectionName: "Coverage Tab",
              });
            }}
          >
            View Declaration of Coverage
          </DSLink>
        </div>
      )}
    </div>
  );

  const Main = () => (
    <Stack>
      <Card>
        <Stack gap="2xl">
          <CoreCoveragesHeader />
          <CoreCoverages
            coveragesList={[...propertyCoverages, ...liabilityCoverages]}
            labelledCoverages={labelledCoverages}
          />
        </Stack>
      </Card>
    </Stack>
  );

  const Sidebar = () => (
    <Stack>
      <Card>
        <Stack gap="2xl">
          <Heading size="md">Key deductibles</Heading>
          <KeyDeductibles
            deductibles={deductibles}
            labelledCoverages={labelledCoverages}
          />
        </Stack>
      </Card>
      <InsuranceRepresentative />
    </Stack>
  );

  return (
    <BaseLayout
      tabs={[
        { name: "Coverage", link: `/my/policies/${ssPolicyId}` },
        { name: "Documents", link: `/my/policies/${ssPolicyId}/documents` },
      ]}
      availableNotifications={[
        "ScheduledPayment",
        "HomeServices",
        "CancellationPayment",
      ]}
    >
      <MainLayout sidebar={<Sidebar />}>
        <Main />
      </MainLayout>
    </BaseLayout>
  );
};
